// src/components/CallLogs.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './CallLogs.css';

function CallLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    // Filter states
    const [filters, setFilters] = useState({
        period: 'today',
        username: '',
        direction: '',
        callerName: '',
        startDate: '',
        endDate: ''
    });

    const [users, setUsers] = useState([]);
    const directions = ['Incomming', 'Outgoing', 'Missed'];

    useEffect(() => {
        // Fetch users for the filter dropdown
        const fetchUsers = async () => {
            try {
                const res = await api.get('/api/users');
                setUsers(res.data);
            } catch (err) {
                console.error('Error fetching users:', err);
            }
        };

        fetchUsers();
        fetchLogs();
    }, [pagination.page, pagination.limit, filters]);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);

        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('page', pagination.page);
            params.append('limit', pagination.limit);
            params.append('period', filters.period);

            if (filters.period === 'custom') {
                if (filters.startDate) params.append('startDate', filters.startDate);
                if (filters.endDate) params.append('endDate', filters.endDate);
            }

            // Continuing from CallLogs.js where we left off
            if (filters.username) params.append('username', filters.username);
            if (filters.direction) params.append('direction', filters.direction);
            if (filters.callerName) params.append('callerName', filters.callerName);

            const res = await api.get(`/api/call-logs?${params.toString()}`);
            setLogs(res.data.logs);
            setPagination(res.data.pagination);
        } catch (err) {
            setError('Failed to fetch call logs');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));

        // Reset to page 1 when filters change
        setPagination(prev => ({
            ...prev,
            page: 1
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchLogs();
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > pagination.totalPages) return;

        setPagination(prev => ({
            ...prev,
            page: newPage
        }));

        /*setTimeout(() => {
            fetchLogs();
        }, 0);*/
    };

    const handleExportCSV = async() => {
        // Build query parameters for CSV export
        const params = new URLSearchParams();
        params.append('period', filters.period);

        if (filters.period === 'custom') {
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
        }

        if (filters.username) params.append('username', filters.username);
        if (filters.direction) params.append('direction', filters.direction);
        if (filters.callerName) params.append('callerName', filters.callerName);

        try {
            const res = await api.get(`/api/export-csv?${params.toString()}`, {
                responseType: 'blob' // important for downloading
            });

            const blob = new Blob([res.data], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'call_logs.csv';
            link.click();
        } catch (err) {
            console.error('Export failed:', err);
            alert('Export failed. Are you logged in?');
        }
    };

    return (
        <div className="call-logs">
            <div className="call-logs-header">
                <h2>Call Logs</h2>
                <button className="export-btn" onClick={handleExportCSV}>
                    Export CSV
                </button>
            </div>

            <div className="filters">
                <form onSubmit={handleSearch}>
                    <div className="filter-row">
                        <div className="filter-group">
                            <label>Time Period</label>
                            <select
                                name="period"
                                value={filters.period}
                                onChange={handleFilterChange}
                            >
                                <option value="today">Today</option>
                                <option value="week">Last Week</option>
                                <option value="month">Last Month</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        {filters.period === 'custom' && (
                            <>
                                <div className="filter-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="filter-row">
                        <div className="filter-group">
                            <label>User</label>
                            <select
                                name="username"
                                value={filters.username}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Users</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.username}>
                                        {user.firstName} {user.lastName} ({user.username})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Direction</label>
                            <select
                                name="direction"
                                value={filters.direction}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Calls</option>
                                {directions.map(dir => (
                                    <option key={dir} value={dir}>{dir}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Caller Name</label>
                            <input
                                type="text"
                                name="callerName"
                                value={filters.callerName}
                                onChange={handleFilterChange}
                                placeholder="Search caller..."
                            />
                        </div>

                        <button type="submit" className="filter-btn">Apply Filters</button>
                    </div>
                </form>
            </div>

            {loading ? (
                <div className="loading">Loading call logs...</div>
            ) : error ? (
                <div className="error">{error}</div>
            ) : logs.length === 0 ? (
                <div className="no-data">No call logs found</div>
            ) : (
                <>
                    <div className="call-logs-table">
                        <table>
                            <thead>
                            <tr>
                                <th>Caller Name</th>
                                <th>Phone Number</th>
                                <th>Date & Time</th>
                                <th>Direction</th>
                                <th>Duration</th>
                                <th>Uploaded By</th>
                            </tr>
                            </thead>
                            <tbody>
                            {logs.map(log => (
                                <tr key={log.id} className={`call-${log.dir.toLowerCase()}`}>
                                    <td>{log.callerName || 'Unknown'}</td>
                                    <td>{log.phNumber}</td>
                                    <td>{log.callDayTime}</td>
                                    <td>{log.dir}</td>
                                    <td>{log.callDuration}</td>
                                    <td>{log.uploader}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="pagination">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.page === 1}
                        >
                            First
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                        >
                            Previous
                        </button>
                        <span>
              Page {pagination.page} of {pagination.totalPages || 1}
            </span>
                        <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.totalPages}
                        >
                            Next
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.page === pagination.totalPages}
                        >
                            Last
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default CallLogs;

